import type { SyncItem } from '../api/types'
import { isIsoDate } from '../lib/days'
import type { TrendMethod } from '../lib/trend'
import { DEFAULT_UNITS, type Units } from '../lib/units'
import { latestById } from './cache'
import { asRecord, foodRefData, isRecord, num, oneOf, readFoodRefs, str, strings, type FoodRef, type JsonObject } from './read'

/** `ActivityLevel` names in shared/, with their multipliers' meaning. */
export const ACTIVITY_LEVELS = [
  { value: 'SEDENTARY', label: 'Sedentary', factor: 1.2 },
  { value: 'LIGHT', label: 'Lightly active', factor: 1.375 },
  { value: 'MODERATE', label: 'Moderately active', factor: 1.55 },
  { value: 'VERY', label: 'Very active', factor: 1.725 },
  { value: 'EXTREME', label: 'Extremely active', factor: 1.9 },
] as const

export type ActivityLevel = (typeof ACTIVITY_LEVELS)[number]['value']
export type Sex = 'male' | 'female'

const ACTIVITY_VALUES = ACTIVITY_LEVELS.map((level) => level.value)
const TREND_METHOD_VALUES: readonly TrendMethod[] = ['CLASSIC', 'LESS_LAG', 'CLASSIC_FAST', 'LESS_LAG_WATER']

/** `Ethnicity` names in shared/ (NICE PH46/NG246's lower-BMI-threshold groups, plus "white or other" and "prefer not to say"). */
export const ETHNICITIES = ['SOUTH_ASIAN', 'CHINESE', 'OTHER_ASIAN', 'BLACK_AFRICAN', 'BLACK_CARIBBEAN', 'MIDDLE_EASTERN', 'WHITE_OR_OTHER', 'PREFER_NOT_TO_SAY'] as const
export type Ethnicity = (typeof ETHNICITIES)[number]

const ETHNICITY_VALUES: readonly Ethnicity[] = ETHNICITIES

/**
 * The `profile` settings group. Fields another client left out read as null; the settings page asks
 * for them before saving. Units and the trend method fall back to kg, kcal, cm and the classic trend.
 */
export interface Profile {
  sex: Sex | null
  birthDate: string | null
  heightCm: number | null
  activityLevel: ActivityLevel | null
  bodyFatPct: number | null
  /** Optional, additive (owner decision 25 Oct 2026): used for nothing but the NICE BMI threshold (ethnicityBmiThreshold). */
  ethnicity: Ethnicity | null
  units: Units
  trendMethod: TrendMethod
  raw: JsonObject
}

export const SHORTCUTS = [
  'search',
  'barcode',
  'label',
  'quickAdd',
  'ai',
  'describe',
  'recipes',
  'library',
  'weight',
  'metrics',
  'water',
  'workout',
  'alcohol',
] as const
export type Shortcut = (typeof SHORTCUTS)[number]

/** How a goal's amounts are meant (`nutrients.goals[*].method`); shared/ NutrientGoalSettings turns them into amounts. */
export const GOAL_METHODS = ['fixed', 'perKg', 'perKcal', 'pctEnergy'] as const
export type GoalMethod = (typeof GOAL_METHODS)[number]

export interface NutrientGoal {
  floor: number | null
  target: number | null
  ceiling: number | null
  method: GoalMethod
  /** ISO date the goal took effect; null for goals saved before dates were kept. */
  since: string | null
}

export interface NutrientSettings {
  pinned: string[]
  goals: Record<string, NutrientGoal>
  /** The group as stored, so fields this version does not know survive a rewrite. */
  raw: JsonObject
}

/**
 * Every settings group, each null (or empty) until some client saves it. `algorithms` is kept as stored: shared/
 * reads it (AlgorithmJson), so unknown fields and values pass through untouched.
 */
export interface Settings {
  profile: Profile | null
  shortcuts: Shortcut[] | null
  favourites: FoodRef[]
  nutrients: NutrientSettings
  algorithms: JsonObject | null
}

/** `profile.units`: each unit falls back to kg, kcal and cm when missing or unknown. */
export function readUnits(value: unknown): Units {
  const units = asRecord(value)
  return {
    weight: oneOf(units.weight, ['kg', 'lb'], DEFAULT_UNITS.weight),
    energy: oneOf(units.energy, ['kcal', 'kj'], DEFAULT_UNITS.energy),
    height: oneOf(units.height, ['cm', 'ftin'], DEFAULT_UNITS.height),
  }
}

export function readProfile(data: JsonObject): Profile {
  const birthDate = str(data.birthDate)
  const heightCm = num(data.heightCm)
  const bodyFatPct = num(data.bodyFatPct)
  return {
    sex: data.sex === 'male' || data.sex === 'female' ? data.sex : null,
    birthDate: birthDate !== null && isIsoDate(birthDate) ? birthDate : null,
    heightCm: heightCm !== null && heightCm > 0 ? heightCm : null,
    activityLevel: ACTIVITY_VALUES.includes(data.activityLevel as ActivityLevel)
      ? (data.activityLevel as ActivityLevel)
      : null,
    bodyFatPct: bodyFatPct !== null && bodyFatPct > 0 && bodyFatPct < 100 ? bodyFatPct : null,
    // Another client's unrecognised value (or none) reads as not answered, same as activityLevel above.
    ethnicity: ETHNICITY_VALUES.includes(data.ethnicity as Ethnicity) ? (data.ethnicity as Ethnicity) : null,
    units: readUnits(data.units),
    trendMethod: oneOf(data.trendMethod, TREND_METHOD_VALUES, 'CLASSIC'),
    raw: data,
  }
}

/** A goal, or null when it cannot be used: not an object, or a method this version does not know (the default applies). */
function readNutrientGoal(value: unknown): NutrientGoal | null {
  if (!isRecord(value)) return null
  const pick = (amount: unknown) => {
    const parsed = num(amount)
    return parsed !== null && parsed >= 0 ? parsed : null
  }
  const method = value.method === undefined || value.method === null ? 'fixed' : GOAL_METHODS.find((known) => known === value.method)
  if (method === undefined) return null
  const since = str(value.since)
  return {
    floor: pick(value.floor),
    target: pick(value.target),
    ceiling: pick(value.ceiling),
    method,
    since: since !== null && isIsoDate(since) ? since : null,
  }
}

export function parseSettings(items: readonly SyncItem[]): Settings {
  const settings: Settings = { profile: null, shortcuts: null, favourites: [], nutrients: { pinned: [], goals: {}, raw: {} }, algorithms: null }
  for (const item of latestById(items, 'SETTINGS')) {
    if (item.deleted) continue
    const data = item.data
    if (item.id === 'profile') {
      settings.profile = readProfile(data)
    } else if (item.id === 'shortcuts') {
      settings.shortcuts = strings(data.items).filter((entry): entry is Shortcut =>
        (SHORTCUTS as readonly string[]).includes(entry),
      )
    } else if (item.id === 'favourites') {
      settings.favourites = readFoodRefs(data.foods)
    } else if (item.id === 'nutrients') {
      const goals: Record<string, NutrientGoal> = {}
      for (const [key, value] of Object.entries(asRecord(data.goals))) {
        const goal = readNutrientGoal(value)
        if (goal) goals[key] = goal
      }
      settings.nutrients = { pinned: strings(data.pinned), goals, raw: data }
    } else if (item.id === 'algorithms') {
      settings.algorithms = data
    }
  }
  return settings
}

function settingsItem(id: string, data: JsonObject, now: number): SyncItem {
  return { type: 'SETTINGS', id, updatedAt: now, deleted: false, data }
}

export function profileItem(profile: Profile, now: number = Date.now()): SyncItem {
  return settingsItem(
    'profile',
    {
      ...profile.raw,
      sex: profile.sex,
      birthDate: profile.birthDate,
      heightCm: profile.heightCm,
      activityLevel: profile.activityLevel,
      bodyFatPct: profile.bodyFatPct,
      ethnicity: profile.ethnicity,
      // Unit fields this version does not know are kept.
      units: { ...asRecord(profile.raw.units), weight: profile.units.weight, energy: profile.units.energy, height: profile.units.height },
      trendMethod: profile.trendMethod,
    },
    now,
  )
}

/** [profile] (an empty one when none is saved yet) with [method] as its trend method. */
export function withTrendMethod(profile: Profile | null, method: TrendMethod): Profile {
  return { ...(profile ?? readProfile({})), trendMethod: method }
}

/**
 * The profile to save so a trend method this browser kept before it was synced ([stored]) moves into the
 * profile; null when there is nothing to move (no or unknown value, or the profile already has it).
 */
export function migratedTrendMethod(stored: string | null, profile: Profile | null): Profile | null {
  const method = TREND_METHOD_VALUES.find((value) => value === stored)
  if (method === undefined || method === (profile?.trendMethod ?? 'CLASSIC')) return null
  return withTrendMethod(profile, method)
}

export function shortcutsItem(shortcuts: readonly Shortcut[], now: number = Date.now()): SyncItem {
  return settingsItem('shortcuts', { items: [...shortcuts] }, now)
}

export function favouritesItem(foods: readonly FoodRef[], now: number = Date.now()): SyncItem {
  return settingsItem('favourites', { foods: foods.map(foodRefData) }, now)
}

/** SETTINGS `algorithms`; [data] comes from shared/ (algorithmsWith), which keeps fields it does not know. */
export function algorithmsItem(data: JsonObject, now: number = Date.now()): SyncItem {
  return settingsItem('algorithms', data, now)
}

/** SETTINGS `nutrients` as shared/ rewrote it (NutrientGoalJson keeps every field it does not change). */
export function nutrientsDataItem(data: JsonObject, now: number = Date.now()): SyncItem {
  return settingsItem('nutrients', data, now)
}

/**
 * SETTINGS `nutrients`. Each goal keeps its stored fields this version does not know; a fixed goal writes no `method`
 * and an undated one no `since`, as shared/ NutrientGoalJson does, so older readers see what they always saw.
 */
export function nutrientsItem(nutrients: NutrientSettings, now: number = Date.now()): SyncItem {
  const stored = asRecord(nutrients.raw.goals)
  const goals: JsonObject = {}
  for (const [key, goal] of Object.entries(nutrients.goals)) {
    const rest = { ...asRecord(stored[key]) }
    delete rest.method
    delete rest.since
    goals[key] = {
      ...rest,
      floor: goal.floor,
      target: goal.target,
      ceiling: goal.ceiling,
      ...(goal.method === 'fixed' ? {} : { method: goal.method }),
      ...(goal.since === null ? {} : { since: goal.since }),
    }
  }
  return settingsItem('nutrients', { ...nutrients.raw, pinned: [...nutrients.pinned], goals }, now)
}

/** Whole years between [birthDate] and [today]. */
export function ageOn(birthDate: string, today: string): number {
  const [birthYear, birthMonth, birthDay] = birthDate.split('-').map(Number) as [number, number, number]
  const [year, month, day] = today.split('-').map(Number) as [number, number, number]
  const hadBirthday = month > birthMonth || (month === birthMonth && day >= birthDay)
  return year - birthYear - (hadBirthday ? 0 : 1)
}

/** Whether two foods are the same: same food id, or same name and brand when neither has an id (Quick Add). */
export function sameFood(ref: Pick<FoodRef, 'foodId' | 'name' | 'brand'>, other: typeof ref): boolean {
  if (ref.foodId !== null || other.foodId !== null) return ref.foodId === other.foodId
  return ref.name === other.name && ref.brand === other.brand
}
